import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { SectionLabel } from '@/components/px/SectionLabel'
import { adminNav } from '@/components/px/nav'
import {
  completeOAuthCallback,
  selectOAuthRedirectAfter,
  selectSealedCredential
} from '@/features/credentials/credentialsSlice'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { useAppDispatch, useAppSelector } from '@/store'

/**
 * `/admin/connectors/callback` (PX-W6.5, AC-4) — the SPA route the provider
 * redirects back to. It reads `code`/`state` from the query and completes the
 * flow with ONE authenticated XHR on the shared client (the provider is
 * registered against THIS route, never against the API — a cross-site
 * top-level navigation carries no Authorization header).
 *
 * The single call is `useRef`-guarded against StrictMode's double-invoked
 * effect. Either param missing renders the error state with NO call. Success
 * routes the user to the flow's `redirectAfter` (the connectors page when
 * null).
 */
export default function OAuthCallbackPage() {
  const { t } = useTranslation('connectors')
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [searchParams] = useSearchParams()
  const code = searchParams.get('code') ?? ''
  const state = searchParams.get('state') ?? ''

  const sealed = useAppSelector(selectSealedCredential)
  const redirectAfter = useAppSelector(selectOAuthRedirectAfter)
  const mutationError = useAppSelector((s) => s.credentials.mutationError)

  const called = useRef(false)

  useEffect(() => {
    if (called.current) return
    if (code === '' || state === '') return
    called.current = true
    void dispatch(completeOAuthCallback({ code, state }))
      .unwrap()
      .then((result) => {
        navigate(result.redirectAfter ?? '/admin/connectors', { replace: true })
      })
      .catch(() => {
        // The typed rejection (e.g. 400 outbound-credential.provider.unsupported)
        // renders below via the mapped mutationError.
      })
  }, [code, state, dispatch, navigate])

  const missingParams = code === '' || state === ''

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={<span>{t('oauth.breadcrumb')}</span>}
      contextPill={t('chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('oauth.title')}</h1>
      </header>

      <section aria-label={t('oauth.title')} className="mt-4 rounded-lg border border-border bg-card px-4 py-5">
        {missingParams ? (
          <>
            <SectionLabel>{t('oauth.failed')}</SectionLabel>
            <p role="alert" className="mt-1.5 text-sm text-blocked">
              {t('oauth.missingParams')}
            </p>
          </>
        ) : mutationError !== null ? (
          <>
            <SectionLabel>{t('oauth.failed')}</SectionLabel>
            <p role="alert" className="mt-1.5 text-sm text-blocked">
              {toMessage(mutationError)}
            </p>
          </>
        ) : sealed !== null ? (
          <>
            <SectionLabel>{t('oauth.sealed')}</SectionLabel>
            <p className="mt-1.5 text-sm text-foreground">
              {t('oauth.sealedBody', { provider: sealed.provider })}
            </p>
            {redirectAfter !== null && (
              <p className="mt-1 text-xs text-muted-foreground">
                {t('oauth.returningTo', { to: redirectAfter })}
              </p>
            )}
          </>
        ) : (
          <>
            <SectionLabel>{t('oauth.completing')}</SectionLabel>
            <p role="status" className="mt-1.5 text-sm text-muted-foreground">
              {t('oauth.completingBody')}
            </p>
          </>
        )}
      </section>
    </AppShell>
  )
}
